// ===================================================================
// AUTH TYPES - Extracted from Backend DTOs
// Source: backend-api/src/auth-2/dtos/ and interfaces/
// ===================================================================

// User Roles - FABRIC BUYER APP ONLY
// This app serves ONLY fabric buyers, no other roles
export enum UserRoles {
  FabricBuyer = 'fabric-buyer', // Only role this app serves
}

// Auth Provider Types
export enum AuthProviderType {
  EMAIL_PASSWORD = 'email_password',
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  PASSWORDLESS = 'passwordless',
  APPLE = 'apple',
}

// User Types
export enum UserType {
  Individual = 'individual',
  Company = 'company',
}

export enum CompanyType {
  SOLE_PROPRIETORSHIP = 'sole_proprietorship',
  PARTNERSHIP = 'partnership',
  LIMITED_LIABILITY = 'limited_liability',
  CORPORATION = 'corporation',
  COOPERATIVE = 'cooperative',
  OTHER = 'other',
}

// ===================================================================
// JWT PAYLOAD INTERFACE
// Source: backend-api/src/auth-2/interfaces/jwt-payload.interface.ts
// ===================================================================

export interface JwtPayload {
  sub: string;              // user ID
  role: UserRoles[];        // all roles the user has
  roles?: UserRoles[];      // alias for role - for backward compatibility
  currentRole: UserRoles;   // active role
  platform: string;         // client platform/app
  tenantId?: string;        // organization ID when applicable
  tenantType?: string;      // type of tenant (brand, marketplace, etc.)
  authProvider: string;     // how the user authenticated
  emailVerified?: boolean;  // Whether email is verified
  phoneVerified?: boolean;  // Whether phone is verified
  profileComplete?: boolean; // Whether profile is complete
  iat?: number;             // issued at
  exp?: number;             // expiration
}

// ===================================================================
// REQUEST DTOs - For API Calls
// Source: backend-api/src/auth-2/dtos/core/
// ===================================================================

// Phone OTP Request (for signup)
export interface RequestPhoneSignupOtpRequest {
  phoneNumber: string;  // with country code e.g. +2347016647855
  platform: string;    // e.g. 'fabrics'
  role: string;         // e.g. 'fabric-buyer'
}

// Complete Phone Signup (with OTP)
export interface CompletePhoneSignupRequest {
  phoneNumber: string;
  otp: string;
  firstName: string;
  lastName: string;
  platform: string;
  role: UserRoles;
  email?: string;
}

// Email/Password Login
export interface EmailPasswordLoginRequest {
  email: string;
  password: string;
  platform: string;
  role: string;
}

// Phone Login (with OTP)
export interface PhoneLoginRequest {
  phoneNumber: string;
  otp: string;
  platform: string;
  role: string;
}

// Request Phone Login OTP
export interface RequestPhoneLoginOtpRequest {
  phoneNumber: string;
  platform: string;
  role: string;
}

// Social Login
export interface SocialLoginRequest {
  provider: AuthProviderType;
  token: string;
  platform: string;
  role: string;
}

// Email/Password Signup
export interface SignupRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  platform: string;
  role: UserRoles;
}

// Verification Requests
export interface RequestEmailVerificationRequest {
  email: string;
  platform?: string;
}

export interface RequestPhoneVerificationRequest {
  phoneNumber: string;
  platform?: string;
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
  platform?: string;
}

export interface VerifyPhoneRequest {
  phoneNumber: string;
  code: string;
  platform?: string;
}

// ===================================================================
// RESPONSE DTOs - From API
// Source: backend-api/src/auth-2/dtos/core/
// ===================================================================

// User Info (embedded in responses)
export interface UserInfo {
  _id?: string;
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  profilePictureUrl?: string;
  role?: UserRoles[];
  currentRole?: UserRoles;
  platform?: string;
  authProvider?: string;
  isEmailVerified?: boolean;
  emailVerified?: boolean;
  isPhoneVerified?: boolean;
  phoneVerified?: boolean;
  profileComplete?: boolean;
}

// Login Response
export interface LoginResponse {
  token: string;
  user: UserInfo;
  currentRole: string;
  availableRoles: string[];
  statusCode?: number;
}

// Signup Response
export interface SignupResponse {
  success: boolean;
  token: string;
  tempToken?: string;
  user: UserInfo;
  nextSteps?: {
    verifyEmail?: boolean;
    verifyPhone?: boolean;
    completeProfile?: boolean;
  };
  message?: string;
  statusCode?: number;
  socialProvider?: string;
  roles: UserRoles[];
}

// Verification Response
export interface VerificationResponse {
  success: boolean;
  message: string;
  token?: string;
  expiresIn?: number; // seconds until expiration
}

// Profile Response
export interface ProfileResponse {
  success: boolean;
  message: string;
  profileComplete: boolean;
  role: string;
  statusCode?: number;
}

// ===================================================================
// PROFILE COMPLETION TYPES
// Source: backend-api/src/auth-2/dtos/core/complete-profile.dto.ts
// ===================================================================

// -------------------------------------------------------------------
// Shared DTOs imported from dedicated modules to avoid duplication
// -------------------------------------------------------------------
import type { LocationDto } from './location';
export type { LocationDto }; // re-export so external imports remain unchanged

export interface WorkingHoursDto {
  day: string;
  isOpen: boolean;
  openTime?: string;
  closeTime?: string;
}

// Base Profile Data
export interface BaseProfileData {
  role: UserRoles;
  userType: UserType;
  // Individual fields
  userGender?: string;
  dateOfBirth?: string;
  // Company fields
  companyName?: string;
  companyType?: CompanyType;
  registrationNumber?: string;
  // Optional fields
  bankAccountNumber?: string;
  bankName?: string;
  bankAccountName?: string;
  referralCode?: string;
  howDidYouHear?: string;
}

// Fabric Buyer Profile - ONLY profile type this app handles
export interface FabricBuyerProfileData extends BaseProfileData {
  role: UserRoles.FabricBuyer;
  location: LocationDto;
  shippingAddress?: LocationDto;
  billingAddress?: LocationDto;
  preferredFabricTypes: string[];
  purchaseFrequency?: string;
  preferredPaymentMethods?: string[];
  businessPurpose?: string;
  preferredFabrics?: string[];
  preferredPaymentMethod?: string;
  purchaseVolume?: string;
  phoneNumber?: string;
}

export interface ReferralCodeOption {
  id: string;
  code: string;
  displayName: string;
}

// Note: Fabric Seller profiles are handled by a different app
// This app only handles Fabric Buyer profiles

// ===================================================================
// UTILITY TYPES
// ===================================================================

// Platform types - FABRIC BUYER APP ONLY
export type Platform = 'fabrics';

// Auth state
export interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  token: string | null;
  currentRole: UserRoles | null;
  availableRoles: UserRoles[];
  loading: boolean;
  error: string | null;
}

// Auth context
export interface AuthContextType extends AuthState {
  login: (credentials: EmailPasswordLoginRequest | PhoneLoginRequest) => Promise<void>;
  logout: () => void;
  signup: (data: SignupRequest | CompletePhoneSignupRequest) => Promise<void>;
  requestOtp: (data: RequestPhoneSignupOtpRequest | RequestPhoneLoginOtpRequest) => Promise<void>;
  verifyEmail: (data: VerifyEmailRequest) => Promise<void>;
  verifyPhone: (data: VerifyPhoneRequest) => Promise<void>;
  switchRole: (role: UserRoles) => Promise<void>;
  refreshToken: () => Promise<void>;
}

// ===================================================================
// ERROR TYPES
// ===================================================================

export interface AuthError {
  code: string;
  message: string;
  statusCode?: number;
  details?: any;
}

// Common auth error codes (matching backend)
export enum AuthErrorCode {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_OTP = 'INVALID_OTP',
  EXPIRED_OTP = 'EXPIRED_OTP',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  PHONE_ALREADY_EXISTS = 'PHONE_ALREADY_EXISTS',
  INVALID_TOKEN = 'INVALID_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  ROLE_NOT_ALLOWED = 'ROLE_NOT_ALLOWED',
  PROFILE_INCOMPLETE = 'PROFILE_INCOMPLETE',
  VERIFICATION_REQUIRED = 'VERIFICATION_REQUIRED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}
